import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { usePoll } from './usePoll';
import { playChime, primeChime } from './chime';
import { hasUnseen, onSeenChange } from './supportSeen';

// The portal has no push channel, so the bell asks. Often enough that a person
// waiting on a reply isn't waiting on the poll as well.
const POLL_MS = 20000;

// Enough to count the queue; the panel only ever lists the newest few.
const PAGE_LIMIT = 50;

function preview(text, max = 64) {
    const clean = String(text || '').replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

// A user is waiting on a reply. The server's `unreadForAdmin` only clears when
// one is sent, so on its own it would keep a conversation on the bell after an
// admin had read it — what has been read is filtered out below.
async function supportItems() {
    const { conversations = [] } = await api('/admin/support');
    return conversations
        .filter((conversation) => conversation.unreadForAdmin)
        .map((conversation) => {
            const messages = conversation.messages || [];
            const last = messages[messages.length - 1];
            return {
                // The last message's timestamp is part of the id on purpose: a
                // fresh reply into a conversation that was already waiting is
                // news, and an id that never changed would swallow the chime.
                id: `support:${conversation.id}:${last?.at || ''}`,
                conversationId: conversation.id,
                kind: 'support',
                title: conversation.user?.name || 'A user',
                detail: preview(last?.text) || 'Started a conversation',
                to: `/support?conversation=${conversation.id}`,
                at: last?.at || null,
            };
        });
}

// A seller is blocked until someone approves the listing, so the queue belongs
// on the bell rather than only on the Listings page.
async function listingItems() {
    const { listings = [] } = await api(`/admin/listings?status=pending&limit=${PAGE_LIMIT}`);
    return listings.map((listing) => ({
        id: `listing:${listing.id}`,
        kind: 'listing',
        title: listing.seller?.businessName || listing.seller?.name || 'A seller',
        detail: `${listing.name} is waiting for review`,
        to: '/listings',
        at: listing.createdAt || null,
    }));
}

const newest = (a, b) => (b.at ? Date.parse(b.at) : 0) - (a.at ? Date.parse(a.at) : 0);

/**
 * Everything waiting on an admin right now, newest first.
 *
 * The count is work outstanding, minus what has already been read: a pending
 * listing clears by being approved or declined, a conversation by being opened
 * (see lib/supportSeen.js) and stays gone until the user says something new.
 *
 * A chime plays when an item appears that this session has not seen before.
 * The first load is the backlog, not news, so it is silent.
 */
export function useNotifications() {
    // What the server last said, kept apart from what is shown: reading a
    // conversation re-filters the list without re-fetching it.
    const [sources, setSources] = useState(null);
    const [items, setItems] = useState([]);
    // Every id already announced this session. It only ever grows: a side that
    // fails to load and comes back must not read as a screenful of new work.
    const announced = useRef(null);

    const load = useCallback(async () => {
        // One source failing still leaves the other worth showing.
        const [support, listings] = await Promise.allSettled([supportItems(), listingItems()]);
        setSources({
            support: support.status === 'fulfilled' ? support.value : [],
            listings: listings.status === 'fulfilled' ? listings.value : [],
        });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!sources) return undefined;

        const derive = () => {
            const visible = [
                ...sources.support.filter((item) => hasUnseen(item.conversationId, item.at)),
                ...sources.listings,
            ].sort(newest);
            setItems(visible);

            if (announced.current === null) {
                announced.current = new Set(visible.map((item) => item.id));
                return;
            }
            const arrived = visible.filter((item) => !announced.current.has(item.id));
            arrived.forEach((item) => announced.current.add(item.id));
            if (arrived.length) playChime();
        };

        derive();
        // Opening a conversation on the Support page next door clears it here
        // too, without waiting for the next poll.
        return onSeenChange(derive);
    }, [sources]);

    // Browsers need an interaction before they will make a sound; catch the
    // first one so the first chime isn't the one that gets swallowed.
    useEffect(() => primeChime(), []);

    usePoll(load, POLL_MS);

    return items;
}
